import { useAdCampaignDetail } from 'data/ad-campaign';
import moment from 'moment';
import React from 'react';
import { EAdCampaignStatus } from 'types/ad-campaign';

import {
  Card, CardContent, Chip, Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

import AdPreview from 'components/AdPreview';
import { useAuthDataContext } from 'components/AuthDataProvider/AuthDataProvider';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';

import {
  enumToReadableName, formatCents, getAgentLandingPageSampleUrlFromType, numberWithCommas,
} from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    flex: 1,
    fontWeight: 400,
  },
  adminInfo: {
    marginTop: theme.spacing(3),
    padding: '12px 16px',
    color: theme.palette.gray.main,
    backgroundColor: theme.palette.grayScale7.main,
    border: `1px solid ${theme.palette.grayScale10.main}`,
    borderRadius: '10px',
    fontSize: '13px',
    lineHeight: '19px',
    overflowWrap: 'anywhere',
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 3fr) minmax(320px, 2fr)',
    gap: theme.spacing(3),
    alignItems: 'start',
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  leftColumn: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  card: {
    backgroundColor: theme.palette.white.main,
    border: `1px solid ${theme.palette.grayScale10.main}`,
    borderRadius: '12px',
    boxShadow: 'none',
  },
  cardContent: {
    padding: theme.spacing(3),
    '&:last-child': {
      paddingBottom: theme.spacing(3),
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
      '&:last-child': {
        paddingBottom: theme.spacing(2),
      },
    },
  },
  cardHeader: {
    marginBottom: theme.spacing(2.5),
  },
  cardTitle: {
    margin: 0,
    color: theme.palette.darkGray.main,
    fontSize: '18px',
    lineHeight: '24px',
    fontWeight: 700,
  },
  cardDescription: {
    margin: '4px 0 0',
    color: theme.palette.lightGray.main,
    fontSize: '14px',
    lineHeight: '20px',
  },
  overviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: `${theme.spacing(3)}px ${theme.spacing(4)}px`,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: theme.spacing(2.5),
    },
  },
  fullWidthDetail: {
    gridColumn: '1 / -1',
  },
  detailLabel: {
    marginBottom: '6px',
    color: theme.palette.lightGray.main,
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  budgetValue: {
    color: theme.palette.darkGray.main,
    fontSize: '22px',
    lineHeight: '28px',
    fontWeight: 700,
  },
  detailSupportingText: {
    marginTop: '2px',
    color: theme.palette.lightGray.main,
    fontSize: '13px',
    lineHeight: '19px',
  },
  statusChip: {
    height: '28px',
    fontWeight: 600,
  },
  statusActive: {
    color: theme.palette.primary.dark,
    backgroundColor: theme.palette.primary.light,
  },
  statusPending: {
    color: theme.palette.darkGray.main,
    backgroundColor: theme.palette.warning.main,
  },
  statusPaused: {
    color: theme.palette.gray.main,
    backgroundColor: theme.palette.grayScale6.main,
  },
  audienceChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  audienceChip: {
    maxWidth: '100%',
    color: theme.palette.gray.main,
    backgroundColor: theme.palette.grayScale7.main,
    border: `1px solid ${theme.palette.grayScale10.main}`,
    fontWeight: 500,
  },
  emptyValue: {
    color: theme.palette.lightGray.main,
    fontSize: '14px',
    lineHeight: '20px',
  },
  destinationLink: {
    display: 'inline-flex',
    alignItems: 'center',
    maxWidth: '100%',
    color: theme.palette.orange.main,
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 600,
    textDecoration: 'underline',
    overflowWrap: 'anywhere',
    '&:hover': {
      color: theme.palette.primary.dark,
    },
    '&:focus-visible': {
      outline: `3px solid ${theme.palette.primary.light}`,
      outlineOffset: '3px',
      borderRadius: '2px',
    },
    '& svg': {
      flexShrink: 0,
      width: '18px',
      height: '18px',
      marginLeft: '6px',
    },
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  metric: {
    minWidth: 0,
    padding: '18px',
    backgroundColor: theme.palette.grayScale7.main,
    borderRadius: '10px',
  },
  metricLabel: {
    color: theme.palette.lightGray.main,
    fontSize: '13px',
    lineHeight: '18px',
    fontWeight: 600,
  },
  metricValue: {
    marginTop: '6px',
    color: theme.palette.darkGray.main,
    fontSize: '26px',
    lineHeight: '32px',
    fontWeight: 700,
    overflowWrap: 'anywhere',
  },
  previewCard: {
    position: 'sticky',
    top: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      position: 'static',
    },
  },
  previewFrame: {
    minHeight: '350px',
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grayScale7.main,
    borderRadius: '10px',
    overflow: 'hidden',
    [theme.breakpoints.down('xs')]: {
      minHeight: '260px',
      padding: theme.spacing(1),
    },
  },
}));

type Props = {
  adCampaignId: string;
};

const formatMetric = (value?: number) => (
  value === undefined || value === null ? 'N/A' : numberWithCommas(value)
);

const getStatusClassName = (
  status: EAdCampaignStatus,
  classes: ReturnType<typeof useStyles>,
) => {
  if (status === EAdCampaignStatus.Active) {
    return `${classes.statusChip} ${classes.statusActive}`;
  }

  if (status === EAdCampaignStatus.Paused) {
    return `${classes.statusChip} ${classes.statusPaused}`;
  }

  return `${classes.statusChip} ${classes.statusPending}`;
};

export default ({ adCampaignId }: Props) => {
  const classes = useStyles();
  const { isAdmin, isBrokerageAdmin } = useAuthDataContext();

  const { adCampaign, isLoadingAdCampaign } = useAdCampaignDetail(adCampaignId);

  if (isLoadingAdCampaign) {
    return (
      <div className={classes.root}>
        <LoadingIndicator />
      </div>
    );
  }

  const destinationUrl = adCampaign.useCustomCallToActionLink
    ? adCampaign.callToActionLink
    : (
      adCampaign.agentLandingPageType
        ? getAgentLandingPageSampleUrlFromType(adCampaign.agentLandingPageType)
        : adCampaign.callToActionLink
    );
  const hasAudience = adCampaign.zipCodes.length > 0 || adCampaign.customAudienceCampaigns.length > 0;
  return (
    <div className={classes.root}>
      {(isAdmin || isBrokerageAdmin) && (
        <div className={classes.adminInfo}>
          <strong>Agent: </strong>
          {`${adCampaign.user.firstName} ${adCampaign.user.lastName} (${adCampaign.user._id})`}
        </div>
      )}

      <div className={classes.layout}>
        <div className={classes.leftColumn}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <header className={classes.cardHeader}>
                <h2 className={classes.cardTitle}>Campaign overview</h2>
                <p className={classes.cardDescription}>
                  Your campaign setup, audience, billing, and destination.
                </p>
              </header>

              <div className={classes.overviewGrid}>
                <div>
                  <div className={classes.detailLabel}>Status</div>
                  <Chip
                    size="small"
                    className={getStatusClassName(adCampaign.status, classes)}
                    label={enumToReadableName(adCampaign.status)}
                  />
                </div>

                <div>
                  <div className={classes.detailLabel}>Monthly budget</div>
                  <div className={classes.budgetValue}>
                    {`${formatCents(adCampaign.costs.userMonthlyBudget)}/mo`}
                  </div>
                  {adCampaign.costs.nextInvoiceDate && (
                    <div className={classes.detailSupportingText}>
                      {`Renews ${moment(adCampaign.costs.nextInvoiceDate).format('MMM D')}`}
                    </div>
                  )}
                </div>

                <div className={classes.fullWidthDetail}>
                  <div className={classes.detailLabel}>Ad audience</div>
                  {hasAudience ? (
                    <div className={classes.audienceChips}>
                      {adCampaign.zipCodes.map((zipCode) => (
                        <Chip
                          key={zipCode}
                          size="small"
                          className={classes.audienceChip}
                          label={`Zip Code ${zipCode}`}
                        />
                      ))}
                      {adCampaign.customAudienceCampaigns.map((campaign) => (
                        <Chip
                          key={campaign._id}
                          size="small"
                          className={classes.audienceChip}
                          label={`${campaign.name} (${campaign.numSubscribedApns} APNs)`}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className={classes.emptyValue}>No audience is configured.</div>
                  )}
                </div>

                <div className={classes.fullWidthDetail}>
                  <div className={classes.detailLabel}>Ad destination</div>
                  {destinationUrl ? (
                    <a
                      className={classes.destinationLink}
                      href={destinationUrl}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Open ad destination in a new tab"
                    >
                      View destination
                      <OpenInNewIcon aria-hidden />
                    </a>
                  ) : (
                    <div className={classes.emptyValue}>No destination is available.</div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <header className={classes.cardHeader}>
                <h2 className={classes.cardTitle}>Performance</h2>
                <p className={classes.cardDescription}>
                  Lifetime delivery results for this digital ad.
                </p>
              </header>

              <div className={classes.metricsGrid}>
                <Tooltip
                  title="The number of unique accounts that saw your ads at least once."
                  TransitionComponent={React.Fragment}
                >
                  <div className={classes.metric}>
                    <div className={classes.metricLabel}>Reach</div>
                    <div className={classes.metricValue}>{formatMetric(adCampaign.stats.reach)}</div>
                  </div>
                </Tooltip>

                <Tooltip
                  title="The number of times your ads were on screen."
                  TransitionComponent={React.Fragment}
                >
                  <div className={classes.metric}>
                    <div className={classes.metricLabel}>Impressions</div>
                    <div className={classes.metricValue}>{formatMetric(adCampaign.stats.impressions)}</div>
                  </div>
                </Tooltip>

                <Tooltip
                  title="The number of call-to-action button link clicks."
                  TransitionComponent={React.Fragment}
                >
                  <div className={classes.metric}>
                    <div className={classes.metricLabel}>Link clicks</div>
                    <div className={classes.metricValue}>
                      {formatMetric(adCampaign.stats.inlineLinkClicks)}
                    </div>
                  </div>
                </Tooltip>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className={`${classes.card} ${classes.previewCard}`}>
          <CardContent className={classes.cardContent}>
            <header className={classes.cardHeader}>
              <h2 className={classes.cardTitle}>Ad preview</h2>
              <p className={classes.cardDescription}>
                Select the creative to open a larger preview.
              </p>
            </header>

            <div className={classes.previewFrame}>
              <AdPreview
                mediaUrl={adCampaign.creative.previewUrl}
                mediaType={adCampaign.creative.mediaType}
                videoThumbnailUrl={adCampaign.creative.videoThumbnailUrl}
                alt={`Preview of ${adCampaign.name}`}
                showMarketingProfileNotice={false}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
